import { Patch } from 'immer';
import { call, select } from 'redux-saga/effects';
import sessionStorageSingleton from '../../../core/configs/sessionStorageSingleton';
import { RootState } from '../../../core/redux/store';
import {
  getUserGroupsDBName,
  getActiveWALLocal, getUserSheetsIdToNameDBName
} from '../../databaseDeprecated/helpers';
import {
  SheetStorageWorkerParams,
  SnapshotParams,
  SnapshotType,
} from '../../databaseDeprecated/types';
import { SheetGroups, EntityIdToName } from '../../types/sheet/sheetGroups';
import { Sheet, UnSavedSheetHistory } from '../../types';
import { PrimaryStorage } from '../../../core/redux/auth/authTypes';
import { UserKeyStore } from '../../../core/database/schemas';
import {
  UnSyncedRecordType,
  saveRecordInUnSyncedDB,
} from '../../../core/database/unSynced/unSyncedStorage';
import { UserStorageParams } from '../../../core/database/user/types';
import {
  putDataInTrash,
  removeDataFromTrash,
} from '../../../core/database/user/userStorage';
import { saveDataInUserStorage } from '../../../core/database/user/helpers';
import { ApplicationType } from '../../../windows/types';
import userStorageWorker from '../../../core/database/user/userWorkerManager';

interface CommonParams {
  primaryStorage: PrimaryStorage;
  userId: string;
}
function* getCommonParams() {
  const commonParams: CommonParams = yield select((state: RootState) => ({
    primaryStorage: state.auth.app.primaryStorage,
    userId: state.auth.user.id,
  }));

  return commonParams;
}

// function* sharedChangesWithOtherTabs(data: Omit<TabSyncedChanges, 'senderId'>) {
//   const userId: string = yield select((state: RootState) => state.auth.user.id);
//   // if (data.changeType !== SnapshotType.EntityIdToName)
//   tabSharedWorker.port.postMessage({
//     type: SharedWorkerMessage.BroadcastChangesToTabs,
//     userId,
//     params: {
//       ...data,
//       senderId: userId,
//     },
//   });
// }

function* startSaveChanges(
  itemId: string,
  feKeyId: string,
  changes: Patch[],
  walType: SnapshotType,
  useWorker: boolean,
  serverAssignedSerial?: number
) {
  // yield sharedChangesWithOtherTabs({
  //   id: itemId,
  //   changeType: walType,
  //   changes,
  // });

  const { primaryStorage }: CommonParams = yield getCommonParams();
  const activeWAL = getActiveWALLocal(itemId);

  const directoryHandle: FileSystemDirectoryHandle | undefined =
    yield sessionStorageSingleton.getDirectoryHandler();
  const feKey: CryptoKey | undefined = yield sessionStorageSingleton.getFeKey(
    feKeyId
  );

  if (!feKey) return 'feKey not found';

  const params: SheetStorageWorkerParams = {
    feKey,
    dbName: itemId,
    walParams: {
      activeWAL,
      changes,
      walType,
      serverAssignedSerial,
    },
    primaryStorage,
    directoryHandle,
  };
  // if (useWorker) sheetSharedStorageWorker.port.postMessage(params);
  // else yield saveDataInSheetStorage(params);
}

export function* startSaveSheetChanges(
  sheetId: string,
  sheetChanges: Patch[],
  useWorker: boolean,
  serverAssignedSerial?: number
) {
  yield call(
    startSaveChanges,
    sheetId,
    sheetId, // In case of sheet feKey id is same as itemId
    sheetChanges,
    SnapshotType.Sheet,
    useWorker,
    serverAssignedSerial
  );
}

// Sub-function to save changes for user groups
export function* startSaveUserGroupsChanges(
  groups: Patch[],
  useWorker: boolean,
  serverAssignedSerial?: number
) {
  const dbId = getUserGroupsDBName();
  yield call(
    startSaveChanges,
    dbId,
    UserKeyStore.UserFeKey,
    groups,
    SnapshotType.Groups,
    useWorker,
    serverAssignedSerial
  );
}

export function* startSaveSheetsIdToNameChanges(
  sheetsIdToName: Patch[],
  useWorker: boolean,
  serverAssignedSerial?: number
) {
  const dbId = getUserSheetsIdToNameDBName();
  yield call(
    startSaveChanges,
    dbId,
    UserKeyStore.UserFeKey,
    sheetsIdToName,
    SnapshotType.EntityIdToName,
    useWorker,
    serverAssignedSerial
  );
}

export function* startSaveSheetRootChanges(
  changes: Patch[],
  useWorker: boolean,
  fromServer?: boolean,
  deletePermanently?: boolean
) {
  const userId: string = yield select((state: RootState) => state.auth.user.id);
  const userFeKey: CryptoKey | undefined =
    yield sessionStorageSingleton.getFeKey(UserKeyStore.UserFeKey);

  // yield sharedChangesWithOtherTabs({
  //   id: 'sheets-root',
  //   changeType: SnapshotType.SheetsRoot,
  //   changes,
  // });
  for (let patch of changes) {
    if (!fromServer)
      yield saveRecordInUnSyncedDB({
        type: UnSyncedRecordType.SheetsRoot,
        changes: [patch],
      });

    const op = patch.op;
    const sheetId = patch.path[0].toString();
    const sheetValue: Sheet = patch.value;
    if (op == 'add') {
      console.log('start save sheet snapshot', sheetValue);
      if (sheetValue) yield startSaveSheetSnapshot(sheetValue, useWorker);
      //Remove from trash
      yield call(removeDataFromTrash, userId, sheetId, ApplicationType.Sheet);
    } else if (op === 'remove') {
      if (deletePermanently) {
        // TODO: Delete permanently
      } else {
        if (userFeKey)
          yield call(putDataInTrash, userId, userFeKey, sheetId, {
            appType: ApplicationType.Sheet,
          });
      }
    }
  }
}

export function* startSaveGroupsSnapshot(
  groups: SheetGroups,
  useWorker: boolean
) {
  const { userId }: CommonParams = yield getCommonParams();
  const feKey: CryptoKey = yield sessionStorageSingleton.getFeKey(
    UserKeyStore.UserFeKey
  );

  if (!feKey) return 'feKey not found';

  const snapshotParams: SnapshotParams = {
    groups,
    snapshotType: SnapshotType.Groups,
  };
  yield startSaveSnapshot(
    getUserGroupsDBName(),
    feKey,
    snapshotParams,
    useWorker
  );
}

export function* startSaveSheetsIdToNameSnapshot(
  sheetsIdToName: EntityIdToName,
  useWorker: boolean
) {
  const { userId }: CommonParams = yield getCommonParams();
  const feKey: CryptoKey = yield sessionStorageSingleton.getFeKey(
    UserKeyStore.UserFeKey
  );

  if (!feKey) return 'feKey not found';

  const snapshotParams: SnapshotParams = {
    sheetsIdToName,
    snapshotType: SnapshotType.EntityIdToName,
  };
  yield startSaveSnapshot(
    getUserSheetsIdToNameDBName(),
    feKey,
    snapshotParams,
    useWorker
  );
}

export function* startSaveSheetSnapshot(sheet: Sheet, useWorker: boolean) {
  const sheetFeKey: CryptoKey | undefined =
    yield sessionStorageSingleton.getFeKey(sheet.id);

  if (!sheetFeKey) return 'feKey not found';

  const snapshotParams: SnapshotParams = {
    sheet,
    snapshotType: SnapshotType.Sheet,
  };
  yield startSaveSnapshot(sheet.id, sheetFeKey, snapshotParams, useWorker);
}

export function* startSaveSnapshot(
  dbName: string,
  feKey: CryptoKey,
  snapshotParams: SnapshotParams,
  useWorker: boolean
) {
  const { primaryStorage } = yield getCommonParams();
  const directoryHandle: FileSystemDirectoryHandle | undefined =
    yield sessionStorageSingleton.getDirectoryHandler();
  const params: SheetStorageWorkerParams = {
    feKey,
    dbName,
    primaryStorage,
    snapshotParams,
    directoryHandle,
  };
  // if (useWorker) sheetSharedStorageWorker.port.postMessage(params);
  // else yield saveDataInSheetStorage(params);
}

export function* startSaveUserSheetHistory(
  userId: string,
  userFeKey: CryptoKey,
  history: UnSavedSheetHistory,
  useWorker: boolean
) {
  const { historyAction, past } = history;
  const params: UserStorageParams = {
    actionType: historyAction,
    userId: userId,
    feKey: userFeKey,
    data: {
      dataType: historyAction,
      past: past,
    },
  };
  if (useWorker) userStorageWorker.postMessage(params);
  else yield saveDataInUserStorage(params);
}
